<script setup>
import { computed, watch } from 'vue'
import { useStore } from 'vuex'
import { Lock, Loader2, Unlock } from 'lucide-vue-next'
import numeral from 'numeral'

import Avatar from '@/components/Avatar.vue'
import PlatformIcon from '@/components/PlatformIcon.vue'

const props = defineProps({
    creator: { type: Object, required: true },
    query: { type: String, required: true },
})

const store = useStore()

const unlockState = computed(
    () => store.getters.unlocks?.[`profile-unlock:superview:${props.creator.platformId}:${props.creator.id}`],
)

const isLegacyBilling = computed(() => store.getters.isLegacyBilling)

const displayName = computed(() => props.creator.displayName || props.creator.handle || 'Unknown creator')
const handle = computed(() => props.creator.handle || props.creator.displayName || '')

const compactFollowers = computed(() => {
    const followers = Number(props.creator.followers || 0)

    if (!followers) return ''
    if (followers >= 1000 && followers % 1000 === 0) return numeral(followers).format('0a')
    if (followers >= 1000) return numeral(followers).format('0.0a').replace('.0', '')

    return numeral(followers).format('0')
})

const normalizedQuery = computed(() => props.query.trim().toLowerCase())

const getHighlightedParts = (value) => {
    if (!value || !normalizedQuery.value) return [{ text: value || '', isMatch: false }]

    const matchIndex = value.toLowerCase().indexOf(normalizedQuery.value)

    if (matchIndex === -1) return [{ text: value, isMatch: false }]

    const matchEnd = matchIndex + normalizedQuery.value.length

    return [
        { text: value.slice(0, matchIndex), isMatch: false },
        { text: value.slice(matchIndex, matchEnd), isMatch: true },
        { text: value.slice(matchEnd), isMatch: false },
    ].filter((part) => part.text)
}

const displayNameParts = computed(() => getHighlightedParts(displayName.value))
const handleParts = computed(() => getHighlightedParts(handle.value))

const fetchRevealStatus = async () => {
    if (!props.creator.platformId || !props.creator.id) return

    await store.dispatch('fetchRevealStatus', {
        quotaType: 'profile-unlock',
        keyIds: ['superview', props.creator.platformId, props.creator.id],
    })
}

watch(() => props.creator.key, fetchRevealStatus, { immediate: true })
</script>

<template>
    <div class="flex min-w-0 flex-1 items-center gap-3">
        <div class="relative shrink-0">
            <Avatar
                class="bg-slate-700 text-slate-50"
                :srcs="[creator.avatarUrl]"
                :username="displayName"
                :size="36"
            />

            <div class="absolute -bottom-1 -right-1 flex rounded-full bg-white">
                <PlatformIcon :platform-id="creator.platformId" class="z-20 h-4 w-4" />
            </div>
        </div>

        <div class="min-w-0 flex-1">
            <div class="truncate text-sm font-medium text-slate-900">
                <template v-for="part in displayNameParts" :key="`${creator.key}:name:${part.text}:${part.isMatch}`">
                    <span :class="{ 'font-semibold text-slate-950': part.isMatch }">{{ part.text }}</span>
                </template>
            </div>
            <div v-if="handle" class="truncate text-xs text-slate-500">
                @<template v-for="part in handleParts" :key="`${creator.key}:handle:${part.text}:${part.isMatch}`">
                    <span :class="{ 'font-medium text-slate-700': part.isMatch }">{{ part.text }}</span>
                </template>
            </div>
        </div>

        <div class="ml-2 flex shrink-0 items-center gap-2">
            <div
                v-if="
                    !isLegacyBilling &&
                    (unlockState?.loading ||
                        unlockState === undefined ||
                        !(unlockState?.is_found && unlockState.unlock?.id))
                "
                class="hidden items-center gap-1.5 text-xs text-slate-500 sm:flex"
            >
                <template v-if="unlockState?.loading || unlockState === undefined">
                    <Loader2 class="h-3.5 w-3.5 animate-spin" />
                    <span>Checking</span>
                </template>
                <template v-else>
                    <Lock class="h-3.5 w-3.5 group-data-[highlighted]:hidden" />
                    <Unlock class="hidden h-3.5 w-3.5 group-data-[highlighted]:block" />
                    <span>Unlock</span>
                </template>
            </div>

            <div class="w-12 text-right text-xs font-medium tabular-nums text-slate-600">
                {{ compactFollowers }}
            </div>
        </div>
    </div>
</template>
